import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { useStickToBottom } from "use-stick-to-bottom";
import { FetchNextPageOptions, InfiniteQueryObserverResult } from "react-query";
import { ChatStatus } from "ai";

import { ActionIcon, Box, Stack, Text } from "@mantine/core";
import { TbArrowDown } from "react-icons/tb";

import ChatInput, { FileUpload } from "../ChatInput";
import ChatModals from "../ChatModals";
import EmptyView from "./EmptyView";
import ChatConversation from "../ChatConversation";

import classes from "./styles.module.css";

import { MosaicUIMessage } from "./useChatThread";
import { ThreadMessagesResponse } from "modules/chat/types/res";

type ChatThreadProps = {
  status: ChatStatus;
  messages: MosaicUIMessage[];
  sendMessage: ReturnType<typeof useChat<MosaicUIMessage>>["sendMessage"];
  stopChat: ReturnType<typeof useChat<MosaicUIMessage>>["stop"];
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isError: boolean;
  emptyView?: {
    title: string;
    description: string;
    context?: string[];
    suggestedMessages?: string[];
  };
  onLoadMore: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<ThreadMessagesResponse, unknown>>;
};

const ChatThread = (props: ChatThreadProps) => {
  const {
    status,
    messages,
    hasNextPage,
    isFetchingNextPage,
    isError,
    emptyView,
    onLoadMore,
    sendMessage,
    stopChat
  } = props;

  const [message, setMessage] = useState("");
  const [reachedBottomOnce, setReachedBottomOnce] = useState(false);

  const chatInputRef = useRef<{ focus: () => void }>(null);

  const { contentRef, scrollRef, scrollToBottom, isNearBottom, isAtBottom } =
    useStickToBottom();

  const fetchMoreHandler = useCallback(async () => {
    const currentHeight = contentRef.current?.getBoundingClientRect().height;

    await onLoadMore();

    const newHeight = contentRef.current?.getBoundingClientRect().height;

    if (scrollRef.current && currentHeight && newHeight) {
      scrollRef.current.scrollTo({
        top: newHeight - currentHeight,
        behavior: "instant"
      });
    }
  }, [contentRef, onLoadMore, scrollRef]);

  const sendHandler = useCallback(
    (text: string, files?: FileUpload[]) => {
      sendMessage({
        text,
        files: files?.length
          ? files.map(file => ({
              url: file.publicURL,
              filename: file.file.name,
              type: "file",
              mediaType: file.file.type
            }))
          : undefined
      });
    },
    [sendMessage]
  );

  useEffect(() => {
    if (status === "submitted") {
      scrollToBottom();
    }
  }, [status, scrollToBottom]);

  useEffect(() => {
    if (isAtBottom) {
      setReachedBottomOnce(true);
    }
  }, [isAtBottom]);

  return (
    <>
      <Box ref={scrollRef} flex={1} className={classes.scrollWrapper}>
        <Stack
          ref={contentRef}
          mih="100%"
          justify={messages.length ? "flex-end" : "center"}
          pos="relative"
        >
          {messages.length ? (
            <ChatConversation
              messages={messages}
              status={status}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              isError={isError}
              reachedBottomOnce={reachedBottomOnce}
              onLoadMore={fetchMoreHandler}
            />
          ) : (
            <EmptyView
              title={emptyView?.title}
              description={emptyView?.description}
              context={emptyView?.context}
              suggestedMessages={emptyView?.suggestedMessages}
              setMessage={msg => {
                setMessage(msg);
                setTimeout(() => {
                  chatInputRef.current?.focus();
                }, 0);
              }}
            />
          )}
        </Stack>
      </Box>

      <Box pos="sticky" bottom={0} left={0} right={0} bg="white" p="md" pt={0}>
        <Text fz="xs" c="dimmed" py="xs">
          Use <b>@</b> to mention a specific project
        </Text>

        <ChatInput
          ref={chatInputRef}
          chatStatus={status}
          message={message}
          setMessage={setMessage}
          onSend={sendHandler}
          onStop={stopChat}
        />

        {!isNearBottom && (
          <ActionIcon
            pos="absolute"
            top={-70}
            left="50%"
            variant="filled"
            color="dark"
            style={{ transform: "translateX(-50%)" }}
            onClick={() => scrollToBottom()}
          >
            <TbArrowDown />
          </ActionIcon>
        )}
      </Box>

      <ChatModals sendMessage={sendMessage} />
    </>
  );
};

export default memo(ChatThread);
